// modules/uploadHandlers.js
import axios from "axios";
import imageCompression from "browser-image-compression";

// Upload a photo (no compression)
export const uploadPhoto = async ({
  docId,
  file,
  formId,
  setLoading,
  setResponseData,
  setSuccess,
}) => {
  if (!file) return;
  // Validate file type and size
  const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif"];
  const allowedMimeTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
  ];
  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  if (
    !allowedExtensions.includes(fileExtension) ||
    !allowedMimeTypes.includes(file.type.toLowerCase())
  ) {
    alert("Unsupported file type. Please upload a JPEG, PNG, or GIF file.");
    return;
  }
  if (file.size > 15 * 1024 * 1024) {
    alert("File size exceeds the 10MB limit.");
    return;
  }

  const formData = new FormData();
  formData.append(`photos[${docId}]`, file, file.name);

  try {
    setLoading(true);
    const uploadResponse = await axios.post(
      `https://formbuilderapi.getforsite.com/api/forms/${formId}/upload-photos`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    const uploadedPhotoUrl = uploadResponse.data.uploadedUrls[docId];
    setResponseData((prev) => {
      const currentPhotos = prev[docId] || [];
      return { ...prev, [docId]: [...currentPhotos, uploadedPhotoUrl] };
    });
    setLoading(false);
    setSuccess(true);
    alert("Photo uploaded successfully.");
  } catch (error) {
    console.error("Error uploading photo:", error);
    setLoading(false);
    alert("An error occurred while uploading the photo.");
  }
};

// Upload multiple photos in parallel (no compression)
export const uploadMultiplePhotos = async ({
  docId,
  files,
  formId,
  setLoading,
  setResponseData,
  setSuccess,
  onProgress, // optional: (uploadedCount, totalCount) => void
}) => {
  if (!files || files.length === 0) return;

  const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif"];
  const allowedMimeTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
  ];

  // Validate all files up-front before any uploads start
  for (const file of files) {
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (
      !allowedExtensions.includes(fileExtension) ||
      !allowedMimeTypes.includes(file.type.toLowerCase())
    ) {
      alert(
        `"${file.name}" is an unsupported file type. Please upload JPEG, PNG, or GIF files only.`,
      );
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      alert(`"${file.name}" exceeds the 15MB size limit.`);
      return;
    }
  }

  setLoading(true);
  let uploadedCount = 0;

  try {
    // Upload every file in parallel; each gets its own FormData + request
    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append(`photos[${docId}]`, file, file.name);

      const uploadResponse = await axios.post(
        `https://formbuilderapi.getforsite.com/api/forms/${formId}/upload-photos`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const url = uploadResponse.data.uploadedUrls[docId];
      uploadedCount += 1;
      onProgress?.(uploadedCount, files.length);
      return url;
    });

    const uploadedUrls = await Promise.all(uploadPromises);

    // Merge all new URLs into the existing array for this field
    setResponseData((prev) => {
      const current = Array.isArray(prev[docId]) ? prev[docId] : [];
      return { ...prev, [docId]: [...current, ...uploadedUrls] };
    });

    setSuccess(true);
    alert(
      uploadedUrls.length === 1
        ? "Photo uploaded successfully."
        : `${uploadedUrls.length} photos uploaded successfully.`,
    );
  } catch (error) {
    console.error("Error uploading photos:", error);
    alert("An error occurred while uploading one or more photos.");
  } finally {
    setLoading(false);
  }
};

// Upload an image (with compression if not a GIF)
export const uploadImage = async ({
  docId,
  file,
  formId,
  setLoading,
  setResponseData,
  setSuccess,
}) => {
  if (!file) {
    console.warn("No file received");
    return;
  }

  const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif"];
  const allowedMimeTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
  ];
  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  if (
    !allowedExtensions.includes(fileExtension) ||
    !allowedMimeTypes.includes(file.type.toLowerCase())
  ) {
    alert("Unsupported file type. Please upload a JPEG, PNG, or GIF file.");
    return;
  }

  if (file.type.toLowerCase() === "image/gif") {
    alert("GIF images will be uploaded as-is, without compression.");
  }

  try {
    setLoading(true);
    let finalFile = file;
    if (file.type.toLowerCase() !== "image/gif") {
      const compressionOptions = {
        maxSizeMB: 4,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      finalFile = await imageCompression(file, compressionOptions);
    }

    if (finalFile.size > 50 * 1024 * 1024) {
      alert("Compressed file still exceeds the 50MB limit.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append(`documents[${docId}]`, finalFile, file.name);

    const uploadResponse = await axios.post(
      `https://formbuilderapi.getforsite.com/api/forms/${formId}/upload-documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    const uploadedImageUrl = uploadResponse.data.uploadedUrls[docId];
    setResponseData((prev) => {
      const currentImages = prev[docId] || [];
      return { ...prev, [docId]: [...currentImages, uploadedImageUrl] };
    });
    setLoading(false);
    setSuccess(true);
    alert("Image uploaded successfully.");
  } catch (error) {
    console.error("Error uploading (or compressing) image:", error);
    setLoading(false);
    alert("An error occurred while uploading the image.");
  }
};

// Upload multiple images in parallel (with compression for non-GIFs)
export const uploadMultipleImages = async ({
  docId,
  files,
  formId,
  setLoading,
  setResponseData,
  setSuccess,
  onProgress,
}) => {
  if (!files || files.length === 0) return;

  const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif"];
  const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

  for (const file of files) {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExtensions.includes(ext) || !allowedMimeTypes.includes(file.type.toLowerCase())) {
      alert(`"${file.name}" is an unsupported file type. Please upload JPEG, PNG, or GIF files only.`);
      return;
    }
  }

  setLoading(true);
  let uploadedCount = 0;

  try {
    const uploadPromises = Array.from(files).map(async (file) => {
      let finalFile = file;
      if (file.type.toLowerCase() !== "image/gif") {
        finalFile = await imageCompression(file, {
          maxSizeMB: 4,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });
      }
      if (finalFile.size > 50 * 1024 * 1024) {
        throw new Error(`"${file.name}" exceeds the 50MB limit after compression.`);
      }
      const formData = new FormData();
      formData.append(`documents[${docId}]`, finalFile, file.name);
      const res = await axios.post(
        `https://formbuilderapi.getforsite.com/api/forms/${formId}/upload-documents`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const url = res.data.uploadedUrls[docId];
      uploadedCount += 1;
      onProgress?.(uploadedCount, files.length);
      return url;
    });

    const uploadedUrls = await Promise.all(uploadPromises);

    setResponseData((prev) => {
      const current = Array.isArray(prev[docId]) ? prev[docId] : [];
      return { ...prev, [docId]: [...current, ...uploadedUrls] };
    });

    setSuccess(true);
  } catch (error) {
    console.error("Error uploading images:", error);
    alert("An error occurred while uploading one or more images.");
  } finally {
    setLoading(false);
  }
};

// Handle file changes for document uploads
export const handleFileChangeUpload = async ({
  docId,
  file,
  formId,
  setLoading,
  setResponseData,
  setSuccess,
}) => {
  if (!file) return;
  const allowedExtensions = [".jpeg", ".jpg", ".png", ".pdf"];
  const allowedMimeTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "application/pdf",
    "application/x-pdf",
    "application/acrobat",
    "applications/vnd.pdf",
    "text/pdf",
    "text/x-pdf",
  ];
  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  if (
    !allowedExtensions.includes(fileExtension) ||
    !allowedMimeTypes.includes(file.type.toLowerCase())
  ) {
    alert("Unsupported file type. Please upload a JPEG, PNG, or PDF file.");
    return;
  }
  if (file.size > 15 * 1024 * 1024) {
    alert("File size exceeds the 15MB limit.");
    return;
  }
  const formData = new FormData();
  formData.append(`documents[${docId}]`, file, file.name);

  try {
    setLoading(true);
    const uploadResponse = await axios.post(
      `https://formbuilderapi.getforsite.com/api/forms/${formId}/upload-documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    const uploadedUrl = uploadResponse.data.uploadedUrls[docId];

    setResponseData((prev) => ({
      ...prev,
      documents: {
        ...(prev.documents || {}),
        [docId]: uploadedUrl,
      },
    }));

    setSuccess(true);
    alert("Document uploaded successfully.");
  } catch (err) {
    console.error("Error uploading document:", err);
    alert("An error occurred while uploading the document.");
  } finally {
    setLoading(false);
  }
};